import React, { useEffect, useState } from 'react';
import { MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideSegmented } from '../../../theme/components/GuideSegmented';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { ProtocolPicker } from './ProtocolPicker';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

type SyncScope = 'all' | 'protocol';

export interface ProviderSyncRequestDrawerProps {
  open: boolean;
  onDismiss: () => void;
  providerName: string;
  protocols: Protocol[];
  protocolsLoading: boolean;
  protocolsError?: unknown;
  /** Pide la sincronización; resuelve al terminar (201 o 409) y rechaza con ErrorType. */
  onSubmit: (protocolId?: number) => Promise<void>;
}

/** "Sincronizar consumos": alcance general (todos los protocolos, 6 meses) o un protocolo (obligatorio). */
export const ProviderSyncRequestDrawer = (props: ProviderSyncRequestDrawerProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.syncDrawer.${path}`, options);

  const [scope, setScope] = useState<SyncScope>('all');
  const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    if (!props.open) return;
    setScope('all');
    setProtocolId(undefined);
    setSubmitted(false);
    setError(undefined);
  }, [props.open]);

  const submit = async () => {
    setSubmitted(true);
    if (scope === 'protocol' && !protocolId) return;
    setSaving(true);
    setError(undefined);
    try {
      await props.onSubmit(scope === 'protocol' ? protocolId : undefined);
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  const protocolError = submitted && scope === 'protocol' && !protocolId ? tr('required') : undefined;

  return (
    <GuideDrawer
      open={props.open}
      size="small"
      onDismiss={props.onDismiss}
      preventDismiss={saving}
      eyebrow={tr('eyebrow', { name: props.providerName })}
      title={tr('title')}
      testId="pcs-sync-drawer"
      primaryAction={{ label: tr('submit'), onClick: submit, loading: saving, testId: 'pcs-sync-submit' }}
    >
      {error !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-sync-request-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('error')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      <GuideDrawerSection title={tr('scopeTitle')} description={tr('description')}>
        <GuideSegmented<SyncScope>
          ariaLabel={tr('scopeTitle')}
          value={scope}
          onChange={(value) => {
            setScope(value);
            setSubmitted(false);
          }}
          options={[
            { value: 'all', label: tr('scopeAll') },
            { value: 'protocol', label: tr('scopeProtocol') },
          ]}
          data-testid="pcs-sync-scope"
        />
        <GuideHelpText>{scope === 'all' ? tr('scopeAllHelp') : tr('scopeProtocolHelp')}</GuideHelpText>
        {scope === 'protocol' && (
          <>
            {props.protocolsError !== undefined && (
              <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-sync-protocols-error">
                <MessageBarBody>
                  <MessageBarTitle>{tr('protocolsError')}</MessageBarTitle>
                  {getApiErrorMessage(props.protocolsError)}
                </MessageBarBody>
              </GuideMessageBar>
            )}
            <GuideField label={tr('protocol')} required error={protocolError} hint={props.protocolsLoading ? tr('loadingProtocols') : undefined}>
              <ProtocolPicker
                protocols={props.protocols}
                value={protocolId}
                onChange={setProtocolId}
                placeholder={tr('protocolPlaceholder')}
                emptyText={tr('noProtocols')}
                disabled={saving || props.protocolsLoading}
                data-testid="pcs-sync-protocol"
              />
            </GuideField>
          </>
        )}
      </GuideDrawerSection>
    </GuideDrawer>
  );
};

export default ProviderSyncRequestDrawer;
